import { Inject, Injectable } from '@angular/core';
import { HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IApiService } from 'core/api/services/api.interface';
import { UserApiService } from 'features/user/api/api-tokens';
import {
  AchBulkActionResponse,
  AchIngestionDetailResponse,
  AchIngestionFilters,
  AchIngestionsResponse,
  AchIngestionSummaryResponse,
  AchPurgeRerunResponse,
  ActiveUsersResponse,
  AuditHistoryResponse,
  AuditQueryParams,
  DateOverrideResponse,
  EnvironmentInfo,
  OpenBatchesResponse,
  InstalledGatewaysResponse,
  RecentTransactionsResponse,
  SessionHistoryResponse,
  TestEmail,
  SendTestEmailRequest,
  TestInvoiceRequest,
  MockHistoryRequest,
  MockCustomersRequest,
  MockCustomersSampleResponse,
} from '../admin-dashboard.model';
import { Profile, UserDetail } from 'features/admin/admin.model';
import type { AchIngestSettingsResponse, AchIngestSettingsValues } from 'features/system-admin/pages/settings/global-settings/ach-ingest-settings.model';
import { IImpersonateResponse } from 'features/user/store/auth.store';
import { FullEncoder } from 'core/api/full-encoder';

// Angular's default HttpUrlEncodingCodec leaves `@ : $ , ; + = ? /` un-encoded;
// email userIds need `@` as %40 so the request reaches the backend correctly.

@Injectable({
  providedIn: 'root',
})
export class AdminDashboardService {
  constructor(
    @Inject(UserApiService) protected apiService: IApiService
  ) {}

  // --- Sessions & Audit ---

  getSessionHistory(
    userId: string,
    limit = 50,
    offset = 0,
    sortField?: string,
    sortOrder?: 'asc' | 'desc',
  ): Observable<SessionHistoryResponse> {
    let params = new HttpParams({ encoder: new FullEncoder() })
      .set('userId', userId)
      .set('limit', limit)
      .set('offset', offset);
    if (sortField) params = params.set('sortField', sortField);
    if (sortOrder) params = params.set('sortOrder', sortOrder);
    return this.apiService.get('adminSessionHistory', { params });
  }

  getActiveUsers(): Observable<ActiveUsersResponse> {
    return this.apiService.get('adminActiveUsers');
  }

  getOpenBatches(): Observable<OpenBatchesResponse> {
    return this.apiService.get('adminOpenBatches');
  }

  getInstalledGateways(): Observable<InstalledGatewaysResponse> {
    return this.apiService.get('adminGateways');
  }

  getRecentTransactions(limit = 50): Observable<RecentTransactionsResponse> {
    const params = new HttpParams({ encoder: new FullEncoder() }).set('limit', limit);
    return this.apiService.get('adminRecentTransactions', { params });
  }

  // --- ACH File Ingestion ---
  getAchIngestionSummary(): Observable<AchIngestionSummaryResponse> {
    return this.apiService.get('adminAchIngestionSummary');
  }

  getAchIngestions(filters: AchIngestionFilters = {}): Observable<AchIngestionsResponse> {
    let params = new HttpParams({ encoder: new FullEncoder() });
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    }
    return this.apiService.get('adminAchIngestions', { params });
  }

  requeueAchIngestion(ingestionId: string): Observable<{ success: boolean; code: number; message: string }> {
    return this.apiService.post('adminAchIngestionRequeue', { urlParams: { ingestionId } });
  }

  /** Clear a file's checksum (on every row sharing it) to unlock requeue. */
  clearAchIngestionChecksum(
    ingestionId: string,
  ): Observable<{ success: boolean; code: number; message: string; cleared?: number }> {
    return this.apiService.post('adminAchIngestionClearChecksum', { urlParams: { ingestionId } });
  }

  /** Bulk requeue selected files (multi-row dashboard action). */
  bulkRequeueAchIngestions(ingestionIds: string[]): Observable<AchBulkActionResponse> {
    return this.apiService.post('adminAchIngestionBulkRequeue', { body: { ingestionIds } });
  }

  /** Bulk reprocess selected files: delete records + re-ingest in place. */
  bulkReprocessAchIngestions(ingestionIds: string[]): Observable<AchBulkActionResponse> {
    return this.apiService.post('adminAchIngestionBulkReprocess', { body: { ingestionIds } });
  }

  /** Per-file detail (result details, orphans, fees) for the drawer. */
  getAchIngestionDetail(ingestionId: string): Observable<AchIngestionDetailResponse> {
    return this.apiService.get('adminAchIngestionDetail', { urlParams: { ingestionId } });
  }

  /**
   * Purge every ACH record for a profile, move its archived/failed files back to
   * the import folder, and trigger the intake job to re-ingest them. Destructive
   * — sends the required `PROCEED` confirmation.
   */
  purgeAndRerunAch(profileId: string): Observable<AchPurgeRerunResponse> {
    return this.apiService.post('adminAchPurgeRerun', {
      urlParams: { profileId },
      body: { confirm: 'PROCEED' },
    });
  }

  getAchIngestionJob(): Observable<any> {
    return this.apiService.get('adminAchIngestionJob');
  }

  setupAchIngestionJob(connectionName?: string): Observable<any> {
    return this.apiService.post('adminAchIngestionJobSetup', {
      body: connectionName ? { connectionName } : {},
    });
  }

  /** ACH ingest processing settings: stored, defaults, resolved + the Paya status-pair table. */
  getAchIngestSettings(): Observable<AchIngestSettingsResponse> {
    return this.apiService.get('adminAchSettings');
  }

  /** Merge a patch into the ACH ingest settings; `null` returns a key to its default. */
  updateAchIngestSettings(patch: Partial<Record<keyof AchIngestSettingsValues, unknown>>): Observable<AchIngestSettingsResponse> {
    return this.apiService.put('adminAchSettings', { body: patch });
  }

  /** Promote every Paya chargeback whose Return Pending window has elapsed. */
  promoteAchReturns(): Observable<any> {
    return this.apiService.post('adminAchPromoteReturns', { body: {} });
  }

  /** Queue a rebuild of ACH current state (+ FreelyPayment funding) as a one-time job. */
  rebuildAchCurrentState(withFunding = true): Observable<any> {
    return this.apiService.post('adminAchRebuildCurrentState', { body: { withFunding } });
  }

  getAuditHistory(params: AuditQueryParams): Observable<AuditHistoryResponse> {
    let httpParams = new HttpParams({ encoder: new FullEncoder() });
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value as string | number | boolean);
      }
    }
    return this.apiService.get('adminAuditHistory', { params: httpParams });
  }

  // --- Users ---

  getUsers(): Observable<UserDetail[]> {
    return this.apiService.get('adminUsers');
  }

  getUser(userNumber: string): Observable<UserDetail> {
    return this.apiService.get('adminUser', {
      urlParams: { userNumber },
    });
  }

  getUserProfiles(userNumber: string): Observable<Profile[]> {
    return this.apiService.get('adminUserProfiles', {
      urlParams: { userNumber },
    });
  }

  getUserProfileRoles(profileId: string, userNumber: string): Observable<string[]> {
    return this.apiService.get('adminProfileUser', {
      urlParams: { profileId, userNumber },
    });
  }

  // --- Profiles ---

  getProfiles(): Observable<Profile[]> {
    return this.apiService.get('adminProfiles');
  }

  createProfile(profile: Partial<Profile>): Observable<Profile> {
    return this.apiService.post('adminProfileCreate', {
      body: profile,
    });
  }

  /**
   * Export a profile as a template. `sections` narrows what is included —
   * omit it for everything. The profile row (general/company/features/routes)
   * is always present, because a template without it is not importable.
   */
  getProfileTemplate(profileId: string, sections?: string[]): Observable<any> {
    return this.apiService.get('adminProfileTemplate', {
      urlParams: { profileId },
      ...(sections?.length
        ? { params: new HttpParams({ encoder: new FullEncoder() }).set('sections', sections.join(',')) }
        : {}),
    });
  }

  createProfileFromTemplate(template: any): Observable<any> {
    return this.apiService.post('adminProfileTemplates', {
      body: template,
    });
  }

  /**
   * Dry run — what each section would do, writing nothing. Backs the wizard's
   * review step. Safe against a profile that does not exist yet.
   */
  previewProfileTemplate(body: any): Observable<any> {
    return this.apiService.post('adminProfileTemplatePreview', { body });
  }

  /**
   * Apply exactly one section to an existing profile — one wizard step.
   * The whole template is posted each time; only `section` is read.
   */
  applyProfileTemplateSection(profileId: string, section: string, body: any): Observable<any> {
    return this.apiService.post('adminProfileTemplateSection', {
      urlParams: { profileId, section },
      body,
    });
  }

  /** Apply a whole template to an EXISTING profile. */
  applyProfileTemplate(profileId: string, body: any): Observable<any> {
    return this.apiService.put('adminProfileTemplateApply', {
      urlParams: { profileId },
      body,
    });
  }

  // --- Test Tools ---

  getTestEmails(): Observable<TestEmail[]> {
    return this.apiService.get('testEmails');
  }

  clearTestEmails(): Observable<any> {
    return this.apiService.delete('testEmails');
  }

  /** Forward a captured mock email to a real address (sends via SendGrid, no capture). */
  sendTestEmail(request: SendTestEmailRequest): Observable<any> {
    return this.apiService.post('testSendEmail', {
      body: request,
    });
  }

  getDateOverride(): Observable<DateOverrideResponse> {
    return this.apiService.get('testDateOverride');
  }

  setDateOverride(active: boolean, offsetSeconds: number): Observable<DateOverrideResponse> {
    return this.apiService.put('testDateOverride', {
      body: { active, offsetSeconds },
    });
  }

  clearDateOverride(): Observable<DateOverrideResponse> {
    return this.apiService.delete('testDateOverride');
  }

  createTestInvoice(invoice: TestInvoiceRequest): Observable<any> {
    return this.apiService.post('testInvoice', {
      body: invoice,
    });
  }

  generateMockHistory(request: MockHistoryRequest): Observable<any> {
    return this.apiService.post('testMockHistory', {
      body: request,
    });
  }

  generateMockCustomers(request: MockCustomersRequest): Observable<any> {
    return this.apiService.post('testMockCustomers', {
      body: request,
    });
  }

  sampleMockCustomers(count = 1): Observable<MockCustomersSampleResponse> {
    const httpParams = new HttpParams().set('count', String(count));
    return this.apiService.get('testMockCustomersSample', { params: httpParams });
  }

  // --- Environment ---

  getEnvironment(): Observable<EnvironmentInfo> {
    return this.apiService.get('systemEnvironment');
  }

  // --- Impersonation ---

  impersonateUser(userId: string): Observable<IImpersonateResponse> {
    return this.apiService.post('systemImpersonate', {
      body: { userId },
    });
  }
}
